/*
  component: template-editor
  title: Structured Writing Prompt Editor
  props:
    - template-type: sections | list
    - template-structure: JSON structure
  methods:
    - getContent: returns assembled markdown
    - setTemplate: configure template
  deps: none
*/
class TemplateEditor extends HTMLElement {
  constructor() {
    super();
    this.templateType = 'simple';
    this.templateStructure = null;
    this.values = {};
  }

  static get observedAttributes() {
    return ['template-type', 'template-structure'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === 'template-type') {
      this.templateType = newValue || 'simple';
    }
    if (name === 'template-structure') {
      try {
        this.templateStructure = newValue ? JSON.parse(newValue) : null;
      } catch {
        this.templateStructure = null;
      }
    }
    if (this.isConnected) this.render();
  }

  connectedCallback() {
    this.render();
  }

  setTemplate(type, structure) {
    this.templateType = type || 'simple';
    this.templateStructure = structure ? this.normalizeStructure(structure) : null;
    this.values = {};
    this.render();
  }

  normalizeStructure(structure) {
    if (!structure || typeof structure !== 'object') {
      return structure;
    }

    if (Array.isArray(structure.sections)) {
      return structure;
    }

    const count = Number(structure.count);
    if (Number.isInteger(count) && count >= 2) {
      return {
        ...structure,
        count
      };
    }

    return structure;
  }

  getContent() {
    if (this.templateType === 'sections' && this.templateStructure?.sections) {
      return this.templateStructure.sections.map((label) => {
        const textarea = this.querySelector(`textarea[data-section="${CSS.escape(label)}"]`);
        const content = textarea?.value.trim() || '';
        return `**${label}:**\n${content}`;
      }).join('\n\n');
    }

    if (this.templateType === 'list' && this.templateStructure?.count) {
      const count = Number(this.templateStructure.count);
      const lines = [];
      for (let i = 1; i <= count; i += 1) {
        const textarea = this.querySelector(`textarea[data-item="${i}"]`);
        const content = textarea?.value.trim() || '';
        lines.push(`${i}. ${content}`);
      }
      return lines.join('\n');
    }

    return '';
  }

  hasAnyContent() {
    return this.getContent().replace(/[*#\d.\s]/g, '').length > 0;
  }

  render() {
    if (this.templateType === 'sections' && this.templateStructure?.sections) {
      this.innerHTML = `
        <div class="template-editor">
          ${this.templateStructure.sections.map((label) => `
            <div class="form-group">
              <label class="form-label" for="section-${this.escapeAttr(label)}">${this.escapeHtml(label)}</label>
              <textarea
                class="form-control"
                data-section="${this.escapeAttr(label)}"
                id="section-${this.escapeAttr(label)}"
                placeholder="Write about ${this.escapeHtml(label.toLowerCase())}..."
                rows="3"
              ></textarea>
            </div>
          `).join('')}
        </div>
      `;
      return;
    }

    if (this.templateType === 'list' && this.templateStructure?.count) {
      const count = Number(this.templateStructure.count);
      if (!Number.isInteger(count) || count < 2) {
        this.innerHTML = '';
        return;
      }

      const label = this.templateStructure.label || 'List items';
      this.innerHTML = `
        <div class="template-editor">
          <div class="template-editor-list">
            ${Array.from({ length: count }, (_, i) => i + 1).map((num) => `
              <div class="form-group">
                <label class="form-label" for="item-${num}">${this.escapeHtml(label)} #${num}</label>
                <textarea
                  class="form-control"
                  data-item="${num}"
                  id="item-${num}"
                  placeholder="Item ${num}..."
                  rows="2"
                ></textarea>
              </div>
            `).join('')}
          </div>
        </div>
      `;
      return;
    }

    this.innerHTML = '';
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  escapeAttr(value) {
    return this.escapeHtml(value).replace(/'/g, '&#39;');
  }
}

customElements.define('template-editor', TemplateEditor);
